import { call, put, take, fork, select, all, takeLatest } from 'redux-saga/effects'
import { takeEvery } from 'redux-saga'
import actionTypes from '../actions/actionTypes'
import callAny from './helpers/call_any'
import formatError from './helpers/error_formatter'
import mixpanel from 'helpers/mixpanel'
import chameleon from 'helpers/chameleon'
import { milisecondsToTime } from 'helpers/dateFormat/dateFormat'
import { adminOnboardingSelectors,
	librariesSelectors,
	myLibrarySelectors,
	onboardingSelectors,
	privateLibrarySelectors,
	storefrontSelectors,
	signupSelectors,
	studentSelectors,
	uiSelectors,
	interactiveVideoSelectors } from 'redux/selectors'
import userPrivateLibrariesSelector from 'redux/selectors/user/user_private_libraries_selector'
import shareLessonSelector from 'redux/selectors/share/share_lesson_selector'
import currentViewMetricsSelector from 'redux/selectors/lessons/current_view_metrics_selector'
import interactiveModeMetricsSelector from 'redux/selectors/interactive_videos/interactive_mode_metrics_selector'
import { metricsVideoSavedDataSelector } from 'redux/selectors/interactive_video_creation'
import bindersSaveFormMetrics from 'redux/selectors/binders/binders_save_form_metrics'
import { privateLibrarySync } from 'sync'
import tooltipsSelector from 'redux/selectors/tooltips/tooltips_selector'
import config from 'config'
import topbarActions from 'redux/actions/topbarActions'
import capitalize from 'helpers/capitalize'
import adminOnboardingActions from 'redux/actions/adminOnboardingActions'
import dmStorageExperimentActions from 'redux/actions/dm_storage_experiment_actions'
import myLibraryActions from 'redux/actions/my_library_actions'
import generalActions from 'redux/actions/generalActions'
import userActions from 'redux/actions/userActions'
import sessionTypes, { sessionTypeNames } from 'helpers/enums/sessionTypes'
import viewTypes from 'helpers/enums/viewTypes'
import loginActions from 'redux/actions/loginActions'
import libraryNameSelector from 'redux/selectors/sidebar/library_name_selector'
import routeActions from 'redux/actions/routeActions'
import mainTopbarActions from 'redux/actions/mainTopbarActions'
import slideTypes from 'helpers/enums/slideTypes'
import slideTypeSelector from 'redux/selectors/student/slide_type_selector'
import navigationMenuActions from 'redux/actions/navigationMenuActions'
import { getRoleNameById } from 'helpers/userProfileRole'
import errorCodes from 'helpers/enums/errorCodes'
import videoSourceTypes from 'helpers/enums/interactiveVideo/videoSourceTypes'
import studentLeadAutofillSelector from 'redux/selectors/student/lead_student_autofill_selector'
import userB2bIsFirstLoginSelector from 'redux/selectors/user/user_b2b_is_first_login_selector'
import videoTrimmingSelector from 'redux/selectors/interactive_video_creation/video_trimming_selector'
import handoutTypes from 'helpers/enums/handoutTypes'
import getLessonCreationMetricsByActionType from 'helpers/getLessonCreationMetricsByActionType'
import lessonCreationLocation from 'helpers/enums/metrics/lessonCreationLocation'
import { languageCodeTypesNames } from 'helpers/enums/interactiveVideo/languageCodeTypes'
import presentationTypes from 'helpers/enums/metrics/presentationTypes'
import videoCreationStatusSelector from 'redux/selectors/interactive_video_creation/video_creation_status_selector'
import getReferenceMediaAddedProperties from 'helpers/metrics/getReferenceMediaAddedProperties'
import interactiveVideoSettingsSaveSelector from 'redux/selectors/interactive_videos/interactive_video_settings_save_selector'
import { VideoStreamModeTypes } from 'helpers/videoStreamMode'
import isCurrentActivityModified from 'redux/selectors/activities/is_current_activity_modified'
import activityOrigins from 'helpers/enums/activities/activityOrigins'
import currentViewSelector from 'redux/selectors/ui/currentViewSelector'
import activityFromCurrentView from 'helpers/metrics/getActivityFromCurrentView'

const sessionTypeValues = {
	[sessionTypes.HOMEWORK]: 'Student Paced',
	[sessionTypes.LIVE_SESSION]: 'Live Lesson',
}

function *identifyUser(action) {
	if (action.payload.presenterUid) {
		yield all([
			call(mixpanel.identify, action.payload.presenterUid),
			call(chameleon.identify, action.payload.presenterUid, getChameleonUserProperties(action)),
		])
		yield put(userActions.identifyUserSuccess())
	}

}

function getChameleonUserProperties(action) {
	if (action.payload && action.payload.chameleonProperties) {
		return action.payload.chameleonProperties
	} else {
		return {}
	}
}

function *getMetrics(entityUID, session_uid = '') {
	let metricsData = {}
	try {
		const metricsResponse = yield callAny(privateLibrarySync.getLessonMetrics, entityUID, session_uid)
		if (metricsResponse && metricsResponse.payload && metricsResponse.payload.metrics) {
			metricsData = metricsResponse.payload.metrics
		}
	} catch (error) {
		const serverError = formatError(error)
		yield put(generalActions.getLessonMetricsError(serverError))
	}
	return metricsData
}

function *getOnboardingType() {
	const adminOnboardingIsAdmin = yield select(adminOnboardingSelectors.getAdminOnboardingIsAdmin)
	return adminOnboardingIsAdmin ? 'Admin Onboarding Express' : 'School User Onboarding'
}

function *trackOnboardingSkip(action) {
	const onboardingData = yield select(onboardingSelectors.getMetricsSelector)
	const properties = {}
	properties['Time to Click'] = Math.round((Date.now() - onboardingData.visit_time) / 1000)
	properties['Welcome Step'] = onboardingData.step
	yield call(mixpanel.track, 'Welcome Skipped', properties, true)
	yield put({
		type: actionTypes.ONBOARDING_SKIP_REDIRECT,
		fromStore: action.fromStore,
	})
}

function *trackOnboardingStep() {
	const onboardingData = yield select(onboardingSelectors.getMetricsSelector)
	if (onboardingData.step === 0) {
		const properties = {}
		properties['Time to Click'] = Math.round((Date.now() - onboardingData.visit_time) / 1000)
		yield call(mixpanel.track, 'Welcome Step 0', properties, false)
	}
}

function *trackOnboardingSaveGrades() {
	const properties = {}
	const onboardingData = yield select(onboardingSelectors.getMetricsSelector)
	const gradesSelector = yield select(onboardingSelectors.getGradesData)
	properties['Time to Click'] = Math.round((Date.now() - onboardingData.visit_time) / 1000)
	properties['Teacher Grades'] = gradesSelector.gradesList
	yield call(mixpanel.track, 'Welcome Step 1', properties, false)
}

function *trackOnboardingSaveSubjects() {
	const properties = {}
	const onboardingData = yield select(onboardingSelectors.getOnboardingViewData)
	const subjectsSelector = yield select(onboardingSelectors.getSubjectsData)
	properties['Time to Click'] = Math.round((Date.now() - onboardingData.visit_time) / 1000)
	properties['Teacher Subjects'] = subjectsSelector.subjectsList
	yield call(mixpanel.track, 'Welcome Step 2', properties, false)
}

function *trackOnboardingAddToLibrary() {
	const properties = {}
	const onboardingData = yield select(onboardingSelectors.getMetricsSelector)
	properties['Time to Add'] = Math.round((Date.now() - onboardingData.visit_time) / 1000)
	properties['Unique Lesson Previews'] = onboardingData.number_of_previews
	yield call(mixpanel.track, 'Welcome Step 3', properties, false)
}

function *trackSignupSuccess(action) {
	let properties = {}
	const signupData = yield select(signupSelectors.signupMetrics)
	const ssoRegistrationSources = {
		Google: 'Google SSO',
		Office: 'Microsoft SSO',
	}
	properties['Referrer Code'] = signupData.referral.status === 'success' ? signupData.referral.value : ''
	if (action.type === actionTypes.SIGNUP_SEND_EMAIL_FORM_SUCCESS) {
		properties['Registration Source'] = 'Email'
	} else if (ssoRegistrationSources[action.data.registrationSource]) {
		properties['Registration Source'] = ssoRegistrationSources[action.data.registrationSource]
	} else { //every new Registration Source should be handled from backend side. ssoRegistrationSources only holds those for compatibility reasons.
		properties['Registration Source'] = action.data.registrationSource
	}
	properties['User ID'] = signupData.presenterUid
	properties['Email'] = action.type === actionTypes.SIGNUP_SEND_EMAIL_FORM_SUCCESS ? action.userEmail : action.data.email
	properties['Subscription Origin'] = 'Silver'
	properties['User Role'] = 'user'
	properties = {...properties, ...action.data.metrics}

	// we have to reset users that are already identified with the presenterUid, because they come from the student ui and have the teacher distinct_id
	const distinctId = yield call(mixpanel.getDistinctId)
	if (distinctId.length === 32) {
		yield call(mixpanel.reset)
	}
	yield call(mixpanel.alias, signupData.presenterUid)
	yield fork(mixpanel.track, 'New User Registered', properties, false)
}

function *trackEntityDownloadedSuccess(action) {
	const properties = {...action.payload.metrics}

	if (action.libraryUID) {
		const userPrivateLibraries = yield select(userPrivateLibrariesSelector)
		const library = userPrivateLibraries.filter(function(item) {
			return item.id === action.libraryUID
		})
		if (library.length) {
			properties['Provenance'] = library[0].title
		} else {
			properties['Provenance'] = 'Public Library'
		}
	}

	const eventName = action.entityType === 'P' ? 'Lesson Downloaded' : 'Bundle Downloaded'
	yield call(mixpanel.track, eventName, properties, false)
}

function *trackProfileImageUpdated() {
	const properties = {}
	yield call(mixpanel.track, 'Add Profile Image', properties, false)
}

function *trackSchoolInfoUpdated() {
	const properties = {}
	let origin = 'My Account'
	const ui = yield select(uiSelectors.uiDataSelector)
	if (ui.currentView === viewTypes.LIBRARY) {
		origin = 'Overlay'
	}
	properties['School Submitted'] = origin
	yield call(mixpanel.track, 'Add School Information', properties, false)
}

function *trackPeopleProperties(action) {
	yield call(mixpanel.setPeopleProperties, action.payload.metrics)
}

function *trackPublicLibraryVisitorOpen() {
	while (true) {
		yield all([
			take(actionTypes.STOREFRONT_VISITOR_LESSONS_FETCHED),
			take(actionTypes.STOREFRONT_VISITOR_DATA_FETCHED),
		])
		const storefrontVisitorProperties = yield select(storefrontSelectors.storefrontVisitorMetrics)
		yield call(mixpanel.track, 'Public Library Visitor Open', storefrontVisitorProperties, false)
	}
}

function *trackPublicLibraryOwnerOpen() {
	yield all([
		take(actionTypes.USER_DATA_FETCHED),
		take(actionTypes.STOREFRONT_OWNER_LESSONS_FETCHED),
		take(actionTypes.STOREFRONT_OWNER_DATA_FETCHED),
		take(actionTypes.MY_ACCOUNT_DATA_FETCHED),
	])
	const storefrontOwnerProperties = yield select(storefrontSelectors.storefrontOwnerMetrics)
	yield call(mixpanel.track, 'Public Library Owner Open', storefrontOwnerProperties, false)
	while (true) {
		yield take(actionTypes.STOREFRONT_OWNER_INITIALIZE)
		yield call(mixpanel.track, 'Public Library Owner Open', storefrontOwnerProperties, false)
	}
}

function *trackEntityAdded() {
	let addedFrom = '', addedTo = ''
	const entitiesProperties = yield select(privateLibrarySelectors.addLessonsMetrics)
	if (entitiesProperties.libraryType === 'school') {
		addedFrom = 'Admin SL'
		addedTo = 'School Library'
	} else if (entitiesProperties.libraryType === 'district') {
		addedFrom = 'Admin DL'
		addedTo = 'District Library'
	}

	if (entitiesProperties.lessons.length >= 1) {
		const lessonsMetrics = yield call(getMetrics, entitiesProperties.lessons)
		yield all(lessonsMetrics.map(lessonMetrics => {
			const lessonProperties = lessonMetrics
			lessonProperties['Lesson Added From'] = addedFrom
			lessonProperties['Lesson Added To'] = addedTo
			lessonProperties['Method'] = 'CTA'
			return call(mixpanel.track, 'Lesson Added', lessonProperties, false)
		}))
	}

	yield all(entitiesProperties.bundles.map(bundle => {
		const bundleProperties = {}
		bundleProperties['Number of Lessons'] = bundle.totalPresentations
		bundleProperties['Bundle Added From'] = addedFrom
		bundleProperties['Bundle Added To'] = addedTo
		bundleProperties['Method'] = 'CTA'
		return call(mixpanel.track, 'Bundle Added', bundleProperties, false)
	}))
}

function *trackLessonAddedFromLibrary(action) {
	const libraryName = capitalize(action.library_type)
	const lessonMetrics = yield call(getMetrics, action.lesson.data.entityUID)
	lessonMetrics['Lesson Added From'] = 'My Library'
	lessonMetrics['Lesson Added To'] = libraryName + ' Library'
	lessonMetrics['Method'] = 'Ellipses'
	yield call(mixpanel.track, 'Lesson Added', lessonMetrics, false)
}

function *trackLessonAddedToFolder(action) {
	const lessonMetrics = {}
	let from = 'My Library'
	let to = 'Folder'
	if (action.type === actionTypes.MY_LIBRARY_REMOVE_LESSON_FROM_FOLDER_SUCCESS) {
		from = 'Folder'
		to = 'My Library'
	}
	lessonMetrics['Lesson Added From'] = from
	lessonMetrics['Lesson Added To'] = to
	lessonMetrics['Method'] = action.method
	yield call(mixpanel.track, 'Lesson Added', lessonMetrics, false)
}

function *trackLessonAddedFromPublicLibrary() {
	const lessonsProperties = yield select(storefrontSelectors.lessonsPickerModalSave)
	const lessonsMetrics = yield call(getMetrics, lessonsProperties.lessonsUIDs)

	yield all(lessonsMetrics.map(lessonMetrics => {
		const properties = lessonMetrics
		properties['Lesson Added From'] = 'Public Library Admin'
		properties['Lesson Added To'] = 'Public Library'
		properties['Method'] = 'CTA'
		return call(mixpanel.track, 'Lesson Added', properties, false)
	}))
}

function *trackLessonAddedFromPrivateLibrary(action) {
	let addedTo = '', provenance = ''
	if (action.libraryType === 'school') {
		addedTo = 'School Library'
	} else if (action.libraryType === 'district') {
		addedTo = 'District Library'
	}

	if (action.currentLibraryUID) {
		const userPrivateLibraries = yield select(userPrivateLibrariesSelector)
		const library = userPrivateLibraries.filter(function(item) {
			return item.id === action.currentLibraryUID
		})
		if (library.length) {
			provenance = library[0].title
		}

	}

	if (action.entityType === 'lesson') {
		const lessonsMetrics = yield call(getMetrics, [action.entityUID])
		yield all(lessonsMetrics.map(lessonMetrics => {
			const lessonProperties = lessonMetrics
			lessonProperties['Lesson Added From'] = 'Preview Content Packages'
			lessonProperties['Lesson Added To'] = addedTo
			lessonProperties['Provenance'] = provenance
			lessonProperties['Method'] = 'CTA'
			return call(mixpanel.track, 'Lesson Added', lessonProperties, false)
		}))

	} else if (action.entityType === 'bundle') {
		const bundleProperties = {}
		bundleProperties['Number of Lessons'] = action.bundlePresentations
		bundleProperties['Bundle Added From'] = 'Preview Content Packages'
		bundleProperties['Bundle Added To'] = addedTo
		bundleProperties['Provenance'] = provenance
		bundleProperties['Method'] = 'CTA'
		yield call(mixpanel.track, 'Bundle Added', bundleProperties, false)
	}
}

function *trackSubPlanCreated(action) {
	const properties = {}
	properties['Sub Plan Name'] = action.name
	yield call(mixpanel.track, 'Sub Plan Created', properties, false)
}

function *trackSubsLessonAdded(action) {
	const lessonsMetrics = yield call(getMetrics, action.presentationUID)
	const properties = lessonsMetrics
	properties['Number of Codes'] = action.self_paceds
	properties['Lesson Added From'] = 'My Library'
	properties['Lesson Added To'] = 'Sub Plan'
	properties['Method'] = 'CTA'
	yield call(mixpanel.track, 'Lesson Added', properties, false)
}

function *trackSubPlanSent(action) {
	const subPlanProperties = yield select(bindersSaveFormMetrics, {entityUID: action.entityUID})
	yield call(mixpanel.track, 'Sub Plan Sent', subPlanProperties, false)
}

function *trackGenericEvent(eventParams) {
	const eventName = eventParams.name
	const properties = eventParams.properties
	yield call(mixpanel.track, eventName, properties, false)
}

function *trackOverlayDistrictLibraryOnboardingConfirm() {
	yield* trackGenericEvent({ name: 'District Library Onboarding Got It Thanks', properties: [] })
}

function *trackOverlayDistrictLibraryOnboardingOpenEvent() {
	yield* trackGenericEvent({ name: 'District Library Onboarding Open', properties: [] })
}

function *trackOverlaySchoolLibraryOnboardingConfirm() {
	yield* trackGenericEvent({ name: 'School Library Onboarding Got It Thanks', properties: [] })
}

function *trackOverlaySchoolLibraryOnboardingOpenEvent() {
	yield* trackGenericEvent({ name: 'School Library Onboarding Open', properties: [] })
}

function *trackOverlayCreateLessonOnboardingConfirm() {
	yield* trackGenericEvent({name: 'Onboarding Overlay Lesson Create Continue', properties: [] })
}

function *trackOverlayCreateLessonOnboardingDismiss() {
	yield* trackGenericEvent({name: 'Onboarding Overlay Lesson Create Dismiss', properties: [] })
}

function *trackOverlayCreateLessonOnboardingInitialize() {
	yield* trackGenericEvent({name: 'Open Onboarding Overlay Lesson Create', properties: [] })
}

function *trackOverlayCreateLessonOnboardingClose() {
	yield* trackGenericEvent({name: 'Close Onboarding Overlay Lesson Create', properties: [] })
}

function *trackSchoolUserOnboardingOverlayClose() {
	yield* trackGenericEvent({ name: 'School User Onboarding Notification Close', properties: [] })
}

function *trackSchoolUserOnboardingOverlayInitialize() {
	yield* trackGenericEvent({ name: 'School User Onboarding Notification Open', properties: [] })
}

function *trackSchoolUserOnboardingOverlayDismiss() {
	yield* trackGenericEvent({ name: 'School User Onboarding Notification No thanks', properties: [] })
}

function *trackSchoolUserOnboardingOverlayConfirm() {
	yield* trackGenericEvent({ name: 'School User Onboarding Notification Get Started', properties: [] })
}
function *trackOverlaySchoolTrialOnboardingConfirm() {
	yield* trackGenericEvent({ name: 'School Free Trial Notification Get Started', properties: [] })
}

function *trackOverlaySchoolTrialOnboardingDismiss() {
	yield* trackGenericEvent({ name: 'School Free Trial Notification No thanks', properties: [] })
}

function *trackOverlaySchoolTrialOnboardingInitialize() {
	yield* trackGenericEvent({ name: 'School Free Trial Notification Open', properties: [] })
}

function *trackOverlaySchoolTrialOnboardingClose() {
	yield* trackGenericEvent({ name: 'School Free Trial Notification Close', properties: [] })
}

function *trackActivitySavedEvent(action) {
	const wasActivityEdited = yield select(isCurrentActivityModified)
	yield call(mixpanel.track, 'Activity Saved', {
		...action.metrics,
		'Activity Bank Content Edited': action.metrics['Activity Origin'] === activityOrigins.ACTIVITY_LIBRARY && wasActivityEdited,
	}, true)
	yield put({
		type: actionTypes.SAVE_ACTIVITY_SUCCESS_REDIRECT,
		redirectUrl: action.redirectUrl,
	})
}

function *trackVideoSavedEvent(action) {
	const savedData = yield select(metricsVideoSavedDataSelector)
	const trimmedData = yield select(videoTrimmingSelector)
	const container = action.type === actionTypes.INTERACTIVE_VIDEO_SAVE
		? 'Video'
		: 'Lesson'

	yield call(mixpanel.track, 'Video Saved', {
		Activities: savedData.totalActivities,
		Container: container,
		Trimmed: trimmedData.isTrimmed,
	})
}

function *trackVideoUploadEvent(status) {
	yield call(mixpanel.track, 'Video Upload', {
		Status: status,
	})
}

function *trackInteractiveVideoUploadCancelEvent() {
	const {isUploading} = yield select(videoCreationStatusSelector)
	yield call(trackVideoUploadEvent, isUploading ? 'Canceled Upload' : 'Canceled Conversion')
}

function *trackVideoPickedEvent(content) {
	yield call(mixpanel.track, 'Video Selected', {
		Content: content,
	})
}

function *trackStoreVideoPickedEvent() {

	yield call(trackVideoPickedEvent, 'Video Library')
}

function *trackMyVideosPickedEvent() {

	yield call(trackVideoPickedEvent, 'My Videos')
}

function *trackHandoutSelected(action) {
	const properties = {}
	properties['Handout Type'] = action.handoutType === handoutTypes.TRANSCRIPT ? 'Transcript' : 'Video Questions'
	yield* trackGenericEvent({ name: 'Handout Download', properties})
}

function *trackSourceVideoPickedEvent(action) {

	const fileEntity = action.file.files[0]
	const content = fileEntity.type === videoSourceTypes.YOUTUBE
		? 'YouTube'
		: 'Upload'

	yield call(trackVideoPickedEvent, content)
}

function *trackUpgradeAction(action) {
	if (action.button_data.buttonMixpanelEvent) {
		yield* trackGenericEvent(action.button_data.buttonMixpanelEvent)
	}
}

function *trackAOXConfirmSchoolOpenEvent() {
	const onboardingType = yield call(getOnboardingType)
	yield call(mixpanel.track, onboardingType + ' Confirm School Open', {}, false)
}

function *trackAOXConfirmSchoolYesEvent(action) {
	const onboardingType = yield call(getOnboardingType)
	const properties = {}
	properties['Trial Length'] = action.durationDays
	yield call(mixpanel.track, onboardingType + ' Confirm School Yes', properties, false)
}

function *trackAOXConfirmSchoolNoEvent() {
	const onboardingType = yield call(getOnboardingType)
	yield call(mixpanel.track, onboardingType + ' Confirm School No', {}, false)
}

function *trackAOXConfirmSchoolNoOpenEvent() {
	yield call(mixpanel.track, 'Admin Onboarding Express School Error Open', {}, false)
}

function *trackAOXWelcomeInviteColleaguesEvent() {
	const adminOnboardingIsAdmin = yield select(adminOnboardingSelectors.getAdminOnboardingIsAdmin)

	if (adminOnboardingIsAdmin) {
		yield call(mixpanel.track, 'Admin Onboarding Express Welcome Invite Colleagues Open', {}, false)
	} else {
		yield call(mixpanel.track, 'School User Onboarding View Colleagues Open', {}, false)
	}
}

function *trackAOXSelectColleaguesEvent() {
	const onboardingType = yield call(getOnboardingType)
	yield call(mixpanel.track, onboardingType + ' Select Colleagues Open', {}, false)
}

function *trackAOXInviteOpenEvent() {
	const properties = {}
	yield call(mixpanel.track, 'Admin Onboarding Express Invite Open', properties, false)
}

function *trackAOXInviteTeacherInvitesEvent(action) {
	const properties = {}
	properties['School Name'] = action.schoolName
	properties['Total Users'] = action.existingUsers + action.newUsers
	properties['Invited without NP'] = action.newUsers
	properties['Invited with NP'] = action.existingUsers
	yield call(mixpanel.track, 'Admin Onboarding Express Teacher Invites', properties, true)
}

function *trackSkipStepAOXEvent(action) {
	const properties = {}
	let trackStep = -1
	if (action.type === actionTypes.ADMIN_ONBOARDING_SKIP_TO_LAST_STEP) {
		trackStep = 3
	} else {
		trackStep = action.step - 1
	}

	properties['Step'] = trackStep
	yield call(mixpanel.track, 'Admin Onboarding Skip This', properties, true)
}

function *trackAdminOnboardingExpressReferContactOpenEvent() {
	const adminOnboardingIsAdmin = yield select(adminOnboardingSelectors.getAdminOnboardingIsAdmin)
	if (adminOnboardingIsAdmin) {
		yield call(mixpanel.track, 'Admin Onboarding Express Refer DM Open', {}, false)
	} else {
		yield call(mixpanel.track, 'School User Onboarding You Did It Open', {}, false)
	}
}

function *trackReferAcontactSentEvent() {
	yield call(mixpanel.track, 'Refer a Contact', {Source: 'Onboarding'}, false)
}

function *trackAdminOnboardingExpressYouDidItOpenEvent() {
	const onboardingType = yield call(getOnboardingType)
	yield call(mixpanel.track, onboardingType + ' You Did It Open', {}, false)
}

function *trackAdminOnboardingExpressCongratsToNearpodLibraryEvent() {
	const onboardingType = yield call(getOnboardingType)
	const adminOnboardingTimeToComplete = yield select(adminOnboardingSelectors.metricAdminOnboarding)
	const properties = {}
	if (adminOnboardingTimeToComplete) {
		properties['TimeToComplete'] = adminOnboardingTimeToComplete
	}
	yield call(mixpanel.track, onboardingType + ' Nearpod Library', properties, false)
}

function *trackReferenceMediaAddedEvent(action) {
	const properties = getReferenceMediaAddedProperties(action)
	yield call(mixpanel.track, 'Reference Media Added', properties, false)
}

function *trackLessonSaved(action) {
	let trackParams = {}
	trackParams['Save Location'] = 'Library'
	if (action.saveResponse.metrics) {
		trackParams = {
			...trackParams,
			...action.saveResponse.metrics,
		}
	}
	yield call(mixpanel.track, 'Lesson Saved', trackParams, false)
}

function *trackLessonLaunchedHomework(action) {
	if (action.payload.pincode) {
		const lessonsMetrics = yield call(getMetrics, [action.presentationUid])
		const currentView = yield select(currentViewMetricsSelector)
		yield all(lessonsMetrics.map(lessonMetrics => {
			const trackParams = lessonMetrics
			trackParams['New Session'] = action.isNew
			trackParams['Lesson Mode'] = 'Student Paced'
			trackParams['Is Tutorial'] = !action.payload.isTutorial
			trackParams['LTI'] = false
			trackParams['Viewport Height'] = window.innerHeight
			trackParams['Viewport Width'] = window.innerWidth
			trackParams['Launched From'] = currentView
			if (action.expire_time) {
				const launched_time = action.launched_time
				const expire_time = action.expire_time
				const endDate = new Date(expire_time * 1000)
				const rangeDate = (expire_time - launched_time)/86400
				const endDateString = endDate.getFullYear()+'/'+(endDate.getMonth()+1)+'/'+ endDate.getDate()
				trackParams['CODE End Date'] = endDateString
				trackParams['CODE length'] = rangeDate
			}
			return call(mixpanel.track, 'Lesson Launched', trackParams, false)
		}))
	}
}

function *trackExtensionLessonLaunched(action) {
	const lessonsMetrics = yield call(getMetrics, [action.presentation_uid])
	const currentView = yield select(currentViewMetricsSelector)
	yield all(lessonsMetrics.map(lessonMetrics => {
		const trackParams = lessonMetrics
		trackParams['New Session'] = true
		trackParams['Lesson Mode'] = action.lessonMode
		trackParams['Is Tutorial'] = action.isTutorial
		trackParams['LTI'] = true
		trackParams['LMS'] = 'Google Classroom'
		trackParams['Viewport Height'] = window.innerHeight
		trackParams['Viewport Width'] = window.innerWidth
		trackParams['Launched From'] = currentView
		return call(mixpanel.track, 'Lesson Launched', trackParams, true)
	}))
	yield put(myLibraryActions.extensionLessonLaunchedSuccess(action.classroomData))
}

function *trackFolderCreated(action) {
	const trackParams = {}
	trackParams['Is Subfolder'] = action.parents > 1
	trackParams['Created From'] = 'My Library'
	trackParams['Folder Name'] = action.response.payload.name
	yield call(mixpanel.track, 'Add Folder', trackParams, false)
}

function *trackLessonEdited(action) {
	const new_expire_time = action.newExpireTime
	const prev_expire_time = action.currentExpireTime
	let newDate = new Date(new_expire_time * 1000)
	newDate = newDate.getFullYear()+'-'+(newDate.getMonth()+1)+'-'+ newDate.getDate()
	let prevDate = new Date(prev_expire_time * 1000)
	prevDate = prevDate.getFullYear()+'-'+(prevDate.getMonth()+1)+'-'+ prevDate.getDate()
	const trackingParams = {}
	trackingParams['Mode'] = 'Student Paced'
	trackingParams['Initial CODE expiration date'] = prevDate
	trackingParams['Updated CODE expiration date'] = newDate
	trackingParams['Cumulative CODE length'] = milisecondsToTime((prev_expire_time * 1000 - Date.now()) / 1000)
	trackingParams['Updated CODE length'] = milisecondsToTime((new_expire_time * 1000 - Date.now()) / 1000)
	yield call(mixpanel.track, 'CODE Expiration Change', trackingParams, false)
}
function *trackContentPackageAccessEvent(action) {
	let event = 'Not Defined'
	const properties = {}
	switch (action.type) {
		case actionTypes.PRIVATE_LIBRARY_UNLOCK_OVERLAY_OPEN:
			event = 'Content Package Access Overlay - Open'
			properties['Unlocked Content Folder'] = action.library || 'Not Defined'
			break
		case actionTypes.PRIVATE_LIBRARY_UNLOCK_SAMPLE_LESSONS:
			event = 'Content Package Access Overlay - Unlock Sample Lessons'
			break
		case actionTypes.PRIVATE_LIBRARY_LEARN_MORE_PACKAGES:
			event = 'Content Package Access Overlay - Learn More'
			break
	}
	yield call(mixpanel.track, event, properties, false)
}

function *trackLoginSignUpEvent() {
	yield call(mixpanel.track, 'Sign Up Log In Page', {}, false)
}

function *trackTopbarSignUpEvent() {
	yield call(mixpanel.track, 'Sign Up for Free Store', {}, true)
	yield put(topbarActions.redirectTo(config.www + 'signup/'))
}

function *trackTopbarSignInEvent() {
	yield call(mixpanel.track, 'Sign In Store', {}, true)
	yield put(topbarActions.redirectTo(config.www + 'login/'))
}

function *trackSignUpPageOpening() {
	yield call(mixpanel.track, 'Sign Up Page Open', {}, false)
}

function *trackDMStorageExperimentReferContact(action) {
	yield call(mixpanel.track, 'December DM Experiment Sent', {'DM Email': action.email, 'DM Role': action.role}, true)
	yield put(dmStorageExperimentActions.referContactMetricsSuccess())
}

function *trackNoStoreDownloadExploreLessons() {
	yield call(mixpanel.track, 'Free Trial Notification - Explore Lessons', {}, true)
	yield put(adminOnboardingActions.noStoreDownloadsExplorerLessonsMetricsSuccess())
}

function *trackNoStoreDownloadGoToMyLibrary() {
	yield call(mixpanel.track, 'Free Trial Notification - Go to My Library', {}, false)
}

function *trackAddTeachersShareColleagues() {
	yield call(mixpanel.track, 'Free Trial Notification - Add Teachers', {}, true)
	yield put(adminOnboardingActions.shareTeachersMetricsSuccess())
}

function *trackAdminOnboardingNoDMInformationReferContact() {
	yield call(mixpanel.track, 'Refer a Contact', {Source: 'Trial'}, true)
	yield put(adminOnboardingActions.noDMInformationReferContactMetricsSuccess())
}

function *trackOverlaySubsDismiss() {
	yield* trackGenericEvent({ name: 'Subs Paywall Not Now', properties: [] })
}

function *trackOverlaySubsInitialize() {
	yield* trackGenericEvent({ name: 'Subs Paywall Open', properties: [] })
}

function *trackOverlaySubsClosed() {
	yield* trackGenericEvent({ name: 'Subs Paywall Close', properties: [] })
}

function *trackOverlaySubsLearnMore(action) {
	yield* trackGenericEvent({ name: 'Subs Paywall Learn More', properties: {'Has Visited Subplans': action.hasVisitedSubplans} })
}

function *trackAdminOnboardingPremiumActivitiesAddLesson() {
	yield call(mixpanel.track, 'Free Trial Notification - Premium Lesson Downloaded', {}, false)
}

function *trackActivitySubmission(activityType, lessonMode, editsCount) {
	const properties = {}
	properties['Activity'] = activityType
	properties['Lesson Mode'] = lessonMode
	properties['Number of Edits'] = editsCount
	yield call(mixpanel.track, 'Student Activity Submission', properties, false)
}

function *trackStudentActivitySubmissionOEQ(action) {
	const lessonMode = action.sessionType === sessionTypes.HOMEWORK ? 'Student Paced' : 'Live Lesson'
	const oeqSubmit = yield select(studentSelectors.oeqSubmitSelector, action.slide_uid)
	yield* trackActivitySubmission('OEQ', lessonMode, oeqSubmit.editsCount)
}

function *trackStudentActivitySubmissionQuiz(action) {
	const lessonMode = action.sessionType === sessionTypes.HOMEWORK ? 'Student Paced' : 'Live Lesson'
	const quizSubmit = yield select(studentSelectors.quizSubmitSelector, action.slide_uid, 0)
	yield *trackActivitySubmission('Quiz', lessonMode, quizSubmit.editsCount)
}

function *trackSaveQuestionFailed(action) {
	const properties = {
		activity_type: 'OEQ',
		error_detail: action.exceedLimitChars ? 'character limit' : 'error at save question',
	}
	yield call(mixpanel.track, 'Activity creation error received', properties)
}

function *trackShareLessonLink(from, lesson, isEditableLink) {
	const lessonsMetrics = yield call(getMetrics, lesson.entityUID)
	const properties = {
		...lessonsMetrics,
	}
	properties['Shared From'] = from
	properties['LessonID'] = lesson.id
	properties['Editable'] = isEditableLink
	yield call(mixpanel.track, 'Share Lesson Link', properties, false)
}

function *trackShareLinkByEntity(action) {
	switch (action.entityType) {
		case 'storefront':
			yield *trackShareStoreFront('Link')
			break
		case 'lesson':
			yield *trackShareLessonLinkFromLibrary(action.entityUID, action.isEditableLink, action.editableLinkAvailable)
			break
	}
}

function *trackShareByEntity(action) {
	if (action.entityType === 'storefront') {
		yield *trackShareStoreFront(action.source, action.count)
	}
}

function *trackShareLessonLinkFromLibrary(entityUID, isEditableLink, editableLinkAvailable) {
	const { entity, share } = yield select(shareLessonSelector, { entityUID, entityType: 'lesson' })
	if (entity) {
		const libraryType = yield select(librariesSelectors.librariesGetCurrentLibraryType)
		const hasEditableUrl = editableLinkAvailable && !!share.payload.link.editable_url
		if (libraryType && hasEditableUrl) {
			yield *trackShareLessonLink(libraryType, entity.data, isEditableLink)
		}
	}
}

function *trackShareStoreFront(source, count = 1) {
	const ownerStateData = yield select(storefrontSelectors.storefrontOwnerMetrics)
	const visitorStateData = yield select(storefrontSelectors.storefrontVisitorMetrics)
	const properties = {}
	let numberOfLessons = 0
	if (ownerStateData['Number of Lessons']) {
		numberOfLessons = ownerStateData['Number of Lessons']
	} else if (visitorStateData['Number of Lessons']) {
		numberOfLessons = visitorStateData['Number of Lessons']
	}
	let numberOfDownloads = 0
	if (ownerStateData['Number of Downloads']) {
		numberOfDownloads = ownerStateData['Number of Downloads']
	} else if (visitorStateData['Number of Downloads']) {
		numberOfDownloads = visitorStateData['Number of Downloads']
	}
	let numberOfFollowers = 0
	if (ownerStateData['Number of Followers']) {
		numberOfFollowers = ownerStateData['Number of Followers']
	} else if (visitorStateData['Number of Followers']) {
		numberOfFollowers = visitorStateData['Number of Followers']
	}
	properties['Number of Lessons'] = numberOfLessons
	properties['Number of Downloads'] = numberOfDownloads
	properties['Number of Followers'] = numberOfFollowers
	properties['Count'] = count
	properties['Method'] = source
	properties['Public Library Owner'] = (ownerStateData['Owner Id'])? ownerStateData['Owner Id'] : visitorStateData['Owner Id']
	yield* trackGenericEvent({ name: 'Share Public Library', properties })
}

function *trackGradesSubjectsOverlayDismiss() {
	yield* trackGenericEvent({ name: 'Grades and Subjects No Thanks', properties: [] })
}

function *trackGradesSubjectsOverlaySubmitted() {
	yield* trackGenericEvent({ name: 'Grades and Subjects Submitted', properties: [] })
}

function *trackShareSession(action) {
	const lesson = yield select(myLibrarySelectors.myLibraryLessonSelector, {entityUID: action.entityUID})
	const lessonsMetrics = yield call(getMetrics, action.entityUID)
	const properties = {
		...lessonsMetrics,
	}
	properties['Method'] = action.method
	properties['Shared From'] = action.sharedFrom
	properties['LessonID'] = lesson.data.id
	yield* trackGenericEvent({ name: 'Share Session', properties })
}

function *trackFirstLessonOverlayFileImport(action) {
	const properties = {}
	const uploadSource = {
		drive: 'Google Drive',
		dropbox: 'DropBox',
		local: 'Local',
		onedrive: 'One Drive',
	}
	const file = action.file.files[0]
	properties['Source'] = uploadSource[file.type]
	if (file.mimeType === 'application/vnd.google-apps.presentation') {
		properties['File Type'] = 'Google Slides'
	} else {
		const validExtensions = ['PNG', 'JPG', 'BMP', 'GIF', 'JPEG', 'PDF', 'MP3', 'OGG',
			'M4A', 'WAV', 'AIFF', 'AIF', 'AC3', 'AAC', 'WMA', 'PPT', 'PPTX']
		const nameSplitted = file.name.split('.')
		const fileExtension = nameSplitted[nameSplitted.length - 1].toUpperCase()
		properties['File Type'] = validExtensions.includes(fileExtension) ? fileExtension : ''
	}
	if (properties['File Type']) {
		yield* trackGenericEvent({ name: 'Import Now', properties})
	}
}

function *trackTimeToClimbOverlayExplore() {
	yield* trackGenericEvent({ name: 'TTC Explore', properties: [] })
}

function *trackTimeToClimbOverlayNoThanks() {
	yield* trackGenericEvent({ name: 'TTC No Thanks', properties: [] })
}

function *trackLoginSSO(action) {
	try {
		const properties = {}
		const redirectUrl = action.redirectUrl ? action.redirectUrl : ''
		const isGoogle = /Google/.test(redirectUrl)
		const isOffice = /Office365/.test(redirectUrl)
		let type = 'Not Defined'
		if (isGoogle) {
			type = 'Google SSO'
		} else if (isOffice) {
			type = 'Microsoft SSO'
		}
		properties['Type'] = type
		yield call(mixpanel.track, 'Sign In', properties, true)
	} catch (error) {
	}
	yield put(loginActions.ssoRedirectMetricsSuccess(action.redirectUrl, action.isInIframe, action.fromLTI))
}

function *trackLoginEmail() {
	const properties = {}
	properties['Type'] = 'Email'
	yield* trackGenericEvent({name: 'Sign In', properties})
}

function *trackCleverLogin(action) {
	if (action.lti.clever === '1') {
		yield* trackGenericEvent({ name: 'Sign In', properties: {Type: 'Clever SSO'} })
	}
}

function *trackSidebarGoToBrowseLessons() {
	const properties = {
		'Clicked From': 'Sidebar',
	}
	yield* trackGenericEvent({
		name: 'Lesson Library Open',
		properties,
	})
	yield put(routeActions.goToStore())
}

function *trackSidebarGoToPublicLibrary() {
	yield* trackGenericEvent({ name: 'Sidebar Public Library click', properties: [] })
	yield put(routeActions.goToPublicLibraryAdmin())
}

function *trackSidebarGoToMyActivities() {
	yield* trackGenericEvent({ name: 'Sidebar My Activities click', properties: [] })
	yield put(routeActions.goToMyActivities())
}

function *trackSidebarGoToSubPlansLibrary() {
	yield* trackGenericEvent({ name: 'Sidebar Sub Plans click', properties: [] })
	yield put(routeActions.goToSubPlans())
}

function *trackSidebarGoToPrivateLibrary(action) {
	const libraryData = yield select(libraryNameSelector(action.libraryId))
	if (libraryData.libraryName) {
		yield* trackGenericEvent({ name: `Sidebar ${libraryData.libraryType} click`, properties: {Name: libraryData.libraryName} })
	} else {
		yield* trackGenericEvent({ name: `Sidebar ${libraryData.libraryType} click`, properties: [] })
	}
	yield put(routeActions.goToPrivateLibrary(action.libraryId))
}

function *trackSidebarGoToReports() {
	yield* trackGenericEvent({ name: 'Sidebar Reports click', properties: [] })
	yield put(routeActions.goToReports())
}

function *trackSidebarGoToTeacherResources() {
	yield* trackGenericEvent({ name: 'Sidebar Teacher Resources click', properties: [] })
	yield put(routeActions.gotToTeacherResources())
}

function *trackGoToMyLibrary() {
	try {
		yield call(mixpanel.track, 'Nearpod home click', [], true)
		yield put(routeActions.goToMyLibrary())
	} catch (error) {
		yield put(mainTopbarActions.homeSelectedFailed(error))
	}
}

function *trackLogoSelected() {
	yield call(mixpanel.track, 'Nearpod logo click', [], true)
}

function *trackBindersOnboarding(action) {
	if (action.step === 2) {
		const properties = {}
		properties['Time to Send'] = action.time
		yield* trackGenericEvent({ name: 'Sample Sub Plan Sent', properties })
	}
}

function *trackLessonCreationOpen(action) {
	let location
	if (action.type === actionTypes.MAIN_TOPBAR_PLUS_ICON_TOGGLED) {
		location = lessonCreationLocation.HEADER
	} else if (action.type === actionTypes.MY_LIBRARY_CREATION_MENU_CREATE_LESSON_SELECTED) {
		location = lessonCreationLocation.MY_LIBRARY
	}
	const properties = { Location: location }
	yield* trackGenericEvent({name: 'Create Click', properties})
}

function *trackToggleQuickLaunchIcon() {
	yield* trackGenericEvent({ name: 'Quick launch click', properties: []})
}

function *trackContentSaved(action) {
	const properties = {}
	let slideType = ''
	switch (action.slideType) {
		case slideTypes.SLIDE_TYPE_ID_FLOCABULARY:
			slideType = 'Flocabulary'
			properties['Title'] = action.title
			break
		default:
			break
	}
	properties['Content'] = slideType
	yield* trackGenericEvent({ name: 'Content Saved', properties})
}

function *trackGoogleSlidesSaveFailed(action) {
	yield* trackGenericEvent({ name: 'Google Slides Save Presentation Failed', properties: action.error})
}

function *trackGoogleSlidesInitializeSaveFailed(action) {
	yield* trackGenericEvent({ name: 'Google Slides Save Initialize Failed', properties: action.error})
}

function *trackOnboardingActivityCreateLessonFailed(action) {
	yield* trackGenericEvent({ name: 'Onboarding Activity Create Lesson Failed', properties: action.error})
}

function *trackOnboardingActivityAddActivity(action) {
	const properties = {}
	properties['Topic'] = action.topic
	yield* trackGenericEvent({ name: 'Onboarding Activity Create Collaborate', properties})
}

function *trackOnboardingActivitySkip() {
	yield* trackGenericEvent({ name: 'Onboarding Activity Onboarding Skip', properties: []})
}

function *trackOnboardingActivityAddActivityFailed(action) {
	yield* trackGenericEvent({ name: 'Onboarding Activity Add Activity Failed', properties: action.error})
}

function *trackOnboardingActivityPostCard(action) {
	const properties = {}
	properties['Text'] = action.text
	yield* trackGenericEvent({ name: 'Onboarding Collaborate Post', properties})
}

function *trackQuickTourStart() {
	try {
		const tooltips = yield select(tooltipsSelector)
		if (tooltips.tooltips.tooltipOBQuickTour) {
			const properties = {}
			let location = 'CTA'
			if (tooltips.tooltips.tooltipOBLiveSession) {
				location = 'Lesson Launch'
			}
			properties['Location'] = location
			yield* trackGenericEvent({name: 'Quick Tour Start', properties})
		}
	} catch (error) {
	}
}

function *trackLimitReachedOverlayNudgeMyAdminSelection() {
	yield* trackGenericEvent({ name: 'Flocab Picker Nudge My Admin', properties: []})
}

function *trackQuickLaunchLaunchLessonFailed(action) {
	yield* trackGenericEvent({ name: 'Quick Launch Launch Lesson Failed', properties: action.error})
}

function getActivityType(type) {
	switch (type) {
		case actionTypes.GOALS_COLLABORATE_CARD_CREATE_LESSON:
		case actionTypes.ONBOARDING_ACTIVITY_ADD_ACTIVITY_SUCCESS:
		case actionTypes.GOALS_COLLABORATE_CARD_LAUNCH_LIVE_LESSON:
			return 'Collaborate'
		case actionTypes.GOALS_TIME_TO_CLIMB_CARD_CREATE_LESSON:
		case actionTypes.GOALS_TIME_TO_CLIMB_CARD_ENABLE_LESSON_SUCCESS:
		case actionTypes.GOALS_TIME_TO_CLIMB_CARD_LAUNCH_LIVE_LESSON:
			return 'Time to Climb'
		case actionTypes.GOALS_OEQ_CARD_CREATE_LESSON_SUCCESS:
		case actionTypes.GOALS_OEQ_CARD_ENABLE_LESSON_SUCCESS:
		case actionTypes.GOALS_OEQ_CARD_LAUNCH_LIVE_LESSON:
			return 'OEQ'
		default:
			return ''
	}
}

function *trackGoalsLessonCreated(action) {
	const properties = {}
	properties['Card Type'] = getActivityType(action.type)
	yield* trackGenericEvent({ name: 'Card Create', properties})
}

function *trackGoalsLessonSaved(action) {
	const properties = {}
	properties['Card Type'] = getActivityType(action.type)
	yield* trackGenericEvent({ name: 'Card Saved', properties})
}

function *trackGoalsLessonLaunched(action) {
	const properties = {}
	properties['Card Type'] = getActivityType(action.type)
	yield* trackGenericEvent({ name: 'Card Launched', properties})
}

function *trackImmersiveReaderOpened(action) {
	const slideType = yield select(slideTypeSelector, action.slideId)
	const properties = {'Slide Type': slideType}
	yield* trackGenericEvent({ name: 'Immersive Reader Open', properties})
}

function *trackAdminDashboardTooltipClick() {
	const properties = {}
	yield* trackGenericEvent({ name: 'Admin Dashboard Tooltip Clicked', properties})
}

function *trackQuickLaunchTimerStart(action) {
	const properties = {}
	properties['Time'] = action.time
	yield* trackGenericEvent({
		name: 'Quick Launch Timer Started',
		properties,
	})
}

function *trackNavigationMenuAccountSelection() {
	yield call(mixpanel.track, 'Manage Account', {}, true)
	yield put(navigationMenuActions.accountMetricsSuccess())
}

function *trackNotificationPreferencesSelected() {
	yield* trackGenericEvent({ name: 'Manage Notifications', properties: []})
}

function *trackNotificationPreferencesEmailSelected(action) {
	const event = action.value ? 'Notifications Turned On' : 'Notifications Turn Off'
	yield* trackGenericEvent({ name: event, properties: []})
}

function *trackNavigationMenuMainLogout(action) {
	const eventName = action.isImpersonate ?
		'End Impersonate' : 'User Logout'
	yield call(mixpanel.track, eventName, {}, true)
	yield put(navigationMenuActions.logoutMetricsSuccess())
}

function *trackNavigationMenuUpgrade() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Upgrade Account',
		properties,
	})
}

function *trackUserSchoolProfileOpen() {
	const properties = {}
	yield* trackGenericEvent({ name: 'Complete Your Profile Open', properties})
}

function *trackUserSchoolProfileSave(action) {
	const properties = {}
	properties['Role'] = getRoleNameById(action.role)
	properties['School Not Found'] = action.schoolId === '-1'
	yield* trackGenericEvent({ name: 'Complete Your Profile Submitted', properties})
}

function *trackNavigationMenuHelpSelected() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Manage Help',
		properties,
	})
}

function *trackNavigationMenuHelpCenterSelected() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Help Center',
		properties,
	})
}

function *trackNavigationMenuTeacherResourcesSelected() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Teacher Resources',
		properties,
	})
}

function *trackNavigationMenuWhatsNewSelected() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'What\'s New',
		properties,
	})
}

function *trackNavigationMenuContactUsSelected() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Contact Us',
		properties,
	})
}

function *trackStorePreviewPageOpen() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Interactive Video Store Preview Page Open',
		properties,
	})
}

function *trackStoreLaunchFrontOfTheClassroom() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Interactive Video Preview Button Click',
		properties,
	})
}

function *trackMyLibraryGoogleSlidesPaywallClick() {
	const properties = { 'Upgrade from': 'My Library' }
	yield* trackGenericEvent({
		name: 'GS Paywall Click',
		properties,
	})
}

function *trackAddonGoogleSlidesPaywallClick() {
	const properties = { 'Upgrade from': 'Add-on' }
	yield* trackGenericEvent({
		name: 'GS Paywall Click',
		properties,
	})
}

function *trackSessionJoin(action) {
	if (action.data.payload) {
		const lessonsMetrics = yield call(getMetrics, action.presentation_uid, action.session_uid)
		const isStudentNameAutofilled = yield select(studentLeadAutofillSelector)
		const properties = {
			...lessonsMetrics,
		}
		properties['Lesson ID'] = action.presentation_uid
		properties['Lesson Mode'] = (action.sessionType === sessionTypes.HOMEWORK) ? 'Student Paced' : 'Live Lesson'
		properties['session_uid'] = action.session_uid
		properties['Verified Student Names'] = isStudentNameAutofilled
		properties['LTI'] = false
		if (action.data.lmsName) {
			properties['LTI'] = true
			properties['LMS'] = action.data.lmsName
		} else if (action.data.payload.lmsName) {
			properties['LTI'] = true
			properties['LMS'] = action.data.payload.lmsName
		}
		yield* trackGenericEvent({
			name: 'Join Session',
			properties,
		})
		if (action.data.payload.reached_valid_session) {
			yield* trackGenericEvent({
				name: 'Valid Session',
				properties,
			})
		}
	}
}

function *trackInteractiveVideoInteractiveModeToggle() {
	const { sessionType, isInteractiveMode } = yield select(interactiveModeMetricsSelector)
	const properties = {}
	properties['on/off'] = isInteractiveMode
	properties['Mode'] = sessionType
	yield* trackGenericEvent({
		name: 'Interactive Mode',
		properties,
	})
}

function *trackNavigationMenuSettingsSelection() {
	yield call(mixpanel.track, 'Teacher Settings', {}, true)
	yield put(navigationMenuActions.settingsMetricsSuccess())
}

function *handleLessonLaunchFailed(action) {
	if (action.error.error_code === errorCodes.LESSON_LAUNCH_NOT_READY) {
		const properties = {
			'Launch Type': action.type,
			'Presentation Uid': action.presentationUid || '',
		}
		yield* trackGenericEvent({
			name: 'Lesson Launch Not Ready',
			properties,
		})
	}
}

function *trackCookieFailed(action) {
	if (action.error.error_code === errorCodes.COOKIES_FAILED) {
		yield* trackGenericEvent({
			name: 'User Cookies Failed',
		})
	}
}

function *trackInteractiveVideoPlayEvent(action) {
	const { sessionType, title } = yield select(interactiveModeMetricsSelector)
	if (sessionType !== sessionTypeNames[sessionTypes.HOMEWORK]) {
		const progressCompleted = action.progressCompleted !== 0 ? `${action.progressCompleted}% completed` : 'Started'
		const properties = {
			'% Completed': progressCompleted,
			title,
			Mode: sessionType,
		}
		yield* trackGenericEvent({
			name: 'Interactive Video Play',
			properties,
		})
	}
}

function *handleGoogleSlidesSaveFailed(action) {
	if (action.error.error_code === errorCodes.GOOGLE_SLIDES_SAVE_ALREADY_PUBLISHED) {
		const properties = {
			['Presentation Uid']: action.entity.data.entityUID,
		}
		yield* trackGenericEvent({
			name: 'Google Presentation Save Failed',
			properties,
		})
	}
}

function *trackNickelTourClose(action) {
	const properties = {
		Step: action.step,
	}
	yield* trackGenericEvent({
		name: 'Nickel Tour Close',
		properties,
	})
}

function *trackNickelTourComplete(action) {
	const properties = {
		'Time to Complete': Math.round(action.timeSpent / 1000),
	}
	yield* trackGenericEvent({
		name: 'Nickel Tour Complete',
		properties,
	})
}

function *trackNickelTourTooltipDismiss() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Collaborate Tool Tip',
		properties,
	})
}

function *trackGoogleSlidesAuthorizeError(action) {
	let properties = {}
	if (action.error.message && action.error.message.includes('Cookies')) {
		properties = {details: action.error.message}
	} else {
		properties = {details: ''}
	}
	yield* trackGenericEvent({
		name: 'Google Slides authorize failed',
		properties,
	})
}

function *trackUserFirstLogin() {
	const [, userExtraData] = yield all([
		take(actionTypes.USER_IDENTIFY_SUCCESS),
		take(actionTypes.USER_EXTRA_DATA),
	])
	const isFirstLogin = yield select(userB2bIsFirstLoginSelector)
	if (isFirstLogin) {
		yield* trackGenericEvent({
			name: 'First Login',
			properties: userExtraData.payload.metrics,
		})
		yield call(chameleon.track, 'First Login')
	}
}

function *trackExploreLibraryThumbnail() {
	const properties = {
		'Clicked From': 'Thumbnail',
	}
	yield* trackGenericEvent({
		name: 'Lesson Library Open',
		properties,
	})
}

function *trackLessonCreation(action) {
	const properties = getLessonCreationMetricsByActionType(action.type)
	yield* trackGenericEvent({name: 'Lesson Created', properties})
}

function *trackEmbedInSlidesSelected() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Embed in slides Open',
		properties,
	})
}

function *trackCaptionsLenguageSelected(action) {
	const captions = yield select(interactiveVideoSelectors.interactiveVideoCaptionsSelector)
	const languageFullName = languageCodeTypesNames[action.language]
	const language = languageFullName || action.language
	const properties = {
		language,
	}
	const captionsStatus = captions.selectedLanguage ? 'Activate' : 'Deactivate'
	const eventName = `Captions ${captionsStatus}`
	yield* trackGenericEvent({
		name: eventName,
		properties,
	})
}

function *trackInteractiveVideoSubmissionSucceeded(action) {
	const activityType = action.type === actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_SUCCESS
		? 'Video Open-Ended Question'
		: 'Video Multiple Choice Question'
	const sessionType = yield select(interactiveVideoSelectors.interactiveVideoSessionTypeSelector)
	yield* trackActivitySubmission(activityType, sessionTypeValues[sessionType], undefined)
}

function *trackOnboardingGuideExploreLessons() {
	const properties = {
		'Clicked From': 'Onboarding Guide',
	}
	yield* trackGenericEvent({
		name: 'Lesson Library Open',
		properties,
	})
}

function *trackMylibraryFolderDeleted() {
	const presentationsCount = yield select(myLibrarySelectors.myLibraryCurrentFolderLessonSelector)

	const properties = {
		location: 'My Library',
		'Number of Lessons': presentationsCount,
	}

	yield* trackGenericEvent({
		name: 'Folder Deleted',
		properties,
	})
}

function *trackMyLibraryLessonDeleted(action) {
	const type = presentationTypes[action.lesson.data.type]
	const properties = {
		location: 'My Library',
		type,
	}
	yield* trackGenericEvent({
		name: 'Lesson Deleted',
		properties,
	})
}

function *trackInteractiveVideoSubmissionFailed(action) {
	const activityType = action.type === actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_FAILED
		? 'Video Open-Ended Question'
		: 'Video Multiple Choice Question'
	const sessionType = yield select(interactiveVideoSelectors.interactiveVideoSessionTypeSelector)
	const properties = {}
	properties['Activity'] = activityType
	properties['Lesson Mode'] = sessionTypeValues[sessionType]
	yield call(mixpanel.track, 'Student Activity Submission Failed', properties, false)
}


function *trackExitSessionPreview() {
	const properties = {}
	yield* trackGenericEvent({
		name: 'Exit Preview',
		properties,
	})
}

function *trackStreamModeUpdated() {
	try {
		const settings = yield select(interactiveVideoSettingsSaveSelector)
		const streamModeTextsByType = {}
		if (settings.isUsingNewStreamModeModal) {
			streamModeTextsByType[VideoStreamModeTypes.TEACHER_DEVICE] = 'Teacher plays'
			streamModeTextsByType[VideoStreamModeTypes.STUDENT_PACED] = 'Student plays'
		} else {
			streamModeTextsByType[VideoStreamModeTypes.ALL_DEVICES] = 'All devices'
			streamModeTextsByType[VideoStreamModeTypes.TEACHER_DEVICE] = 'This device only'
		}
		const properties = {
			Mode: streamModeTextsByType[settings.streamMode],
		}
		yield call(mixpanel.track, 'Interactive Video Playback Mode Selected', properties, false)
	// eslint-disable-next-line no-empty
	} catch (error) {}
}

function *trackActivityBanksActivitySelected() {
	const currentView = yield select(currentViewSelector)
	const activityType = activityFromCurrentView(currentView)
	yield call(mixpanel.track, 'Activity Selected from Bank', {'Activity type': activityType }, true)
}

export default [
	takeEvery(actionTypes.USER_DATA_FETCHED, identifyUser),
	takeEvery(actionTypes.ONBOARDING_SKIP, trackOnboardingSkip),
	takeEvery(actionTypes.ONBOARDING_NAVIGATE_TO_STEP, trackOnboardingStep),
	takeEvery(actionTypes.ONBOARDING_SAVE_GRADES, trackOnboardingSaveGrades),
	takeEvery(actionTypes.ONBOARDING_SAVE_SUBJECTS, trackOnboardingSaveSubjects),
	takeEvery(actionTypes.ONBOARDING_ADD_TO_LIBRARY, trackOnboardingAddToLibrary),
	takeEvery([actionTypes.SIGNUP_SEND_EMAIL_FORM_SUCCESS, actionTypes.SIGNUP_SEND_SSO_FORM_SUCCESS/*, actionTypes.USER_DATA_FETCHED*/], trackSignupSuccess),
	takeEvery(actionTypes.CART_BUY_PRODUCT_SUCCESS, trackEntityDownloadedSuccess),
	takeEvery(actionTypes.MY_ACCOUNT_SAVE_ICON_SUCCESS, trackProfileImageUpdated),
	takeEvery(actionTypes.MY_ACCOUNT_SAVE_SCHOOL_INFO_SUCCESS, trackSchoolInfoUpdated),
	takeEvery(actionTypes.USER_EXTRA_DATA, trackPeopleProperties),
	fork(trackPublicLibraryVisitorOpen),
	fork(trackPublicLibraryOwnerOpen),
	takeEvery(actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_GET_METRICS, trackEntityAdded),
	takeEvery(actionTypes.STOREFRONT_LESSONS_PICKER_SAVE_LESSONS, trackLessonAddedFromPublicLibrary),
	takeEvery(actionTypes.PRIVATE_LIBRARY_ADD_TO_PRIVATE_LIBRARY, trackLessonAddedFromPrivateLibrary),
	takeEvery([actionTypes.MY_LIBRARY_ADD_LESSON_TO_PRIVATE_LIBRARY_SUCCESS, actionTypes.MY_LIBRARY_ADD_LESSON_TO_PUBLIC_LIBRARY_SUCCESS], trackLessonAddedFromLibrary),
	takeEvery([actionTypes.MY_LIBRARY_ADD_LESSON_TO_FOLDER_SUCCESS, actionTypes.MY_LIBRARY_MOVE_LESSON_SUCCESS, actionTypes.MY_LIBRARY_LESSONS_PICKER_SAVE_LESSONS_SUCCESS, actionTypes.MY_LIBRARY_REMOVE_LESSON_FROM_FOLDER_SUCCESS], trackLessonAddedToFolder),
	takeEvery(actionTypes.BINDERS_BINDER_CREATE, trackSubPlanCreated),
	takeEvery(actionTypes.BINDERS_SAVE_NOTES, trackSubsLessonAdded),
	takeEvery(actionTypes.BINDERS_SHARE_FORM_SUCCESS, trackSubPlanSent),
	takeEvery([actionTypes.USER_UPGRADE_ACTION_SELECTED], trackUpgradeAction),
	takeLatest(actionTypes.OVERLAY_SCHOOL_LIBRARY_ONBOARDING_INITIALIZE, trackOverlaySchoolLibraryOnboardingOpenEvent),
	takeLatest(actionTypes.OVERLAY_DISTRICT_LIBRARY_ONBOARDING_INITIALIZE, trackOverlayDistrictLibraryOnboardingOpenEvent),
	takeLatest(actionTypes.OVERLAY_SCHOOL_LIBRARY_ONBOARDING_CONFIRM, trackOverlaySchoolLibraryOnboardingConfirm),
	takeLatest(actionTypes.OVERLAY_DISTRICT_LIBRARY_ONBOARDING_CONFIRM, trackOverlayDistrictLibraryOnboardingConfirm),
	takeLatest(actionTypes.MY_LIBRARY_ONBOARDING_CREATE_LESSON_OVERLAY_CONFIRM, trackOverlayCreateLessonOnboardingConfirm),
	takeLatest(actionTypes.MY_LIBRARY_ONBOARDING_CREATE_LESSON_OVERLAY_DISMISS, trackOverlayCreateLessonOnboardingDismiss),
	takeLatest(actionTypes.MY_LIBRARY_ONBOARDING_CREATE_LESSON_OVERLAY_CLOSE, trackOverlayCreateLessonOnboardingClose),
	takeLatest(actionTypes.MY_LIBRARY_ONBOARDING_CREATE_LESSON_OVERLAY_INITIALIZE, trackOverlayCreateLessonOnboardingInitialize),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_USER_ONBOARDING_OVERLAY_CLOSE, trackSchoolUserOnboardingOverlayClose),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_USER_ONBOARDING_OVERLAY_INITIALIZE, trackSchoolUserOnboardingOverlayInitialize),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_USER_ONBOARDING_OVERLAY_CONFIRM, trackSchoolUserOnboardingOverlayConfirm),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_USER_ONBOARDING_OVERLAY_DISMISS, trackSchoolUserOnboardingOverlayDismiss),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_TRIAL_ONBOARDING_OVERLAY_CONFIRM, trackOverlaySchoolTrialOnboardingConfirm),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_TRIAL_ONBOARDING_OVERLAY_DISMISS, trackOverlaySchoolTrialOnboardingDismiss),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_TRIAL_ONBOARDING_OVERLAY_INITIALIZE, trackOverlaySchoolTrialOnboardingInitialize),
	takeLatest(actionTypes.MY_LIBRARY_SCHOOL_TRIAL_ONBOARDING_OVERLAY_CLOSE, trackOverlaySchoolTrialOnboardingClose),
	takeEvery([
		actionTypes.QUIZ_SAVE_QUIZ_SUCCESS,
		actionTypes.DRAW_IT_SAVE_DRAW_IT_SUCCESS,
		actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_SUCCESS,
		actionTypes.POLL_SAVE_POLL_SUCCESS,
		actionTypes.OEQ_SAVE_OEQ_SUCCESS,
	], trackActivitySavedEvent),
	takeEvery([
		actionTypes.QUIZ_UPLOAD_REFERENCE_MEDIA,
		actionTypes.DRAW_IT_UPLOAD_REFERENCE_MEDIA,
		actionTypes.POLL_UPLOAD_REFERENCE_MEDIA,
		actionTypes.OEQ_UPLOAD_REFERENCE_MEDIA,
	], trackReferenceMediaAddedEvent),
	takeEvery(actionTypes.MY_LIBRARY_SAVE_CHANGES_FETCHED, trackLessonSaved),
	takeEvery(actionTypes.MY_LIBRARY_HOMEWORK_FETCHED, trackLessonLaunchedHomework),
	takeEvery(actionTypes.MY_LIBRARY_EXTENSION_SESSION_FETCHED, trackExtensionLessonLaunched),
	takeEvery(actionTypes.MY_LIBRARY_ADD_OR_UPDATE_FOLDER_SUCCESS, trackFolderCreated),
	takeEvery(actionTypes.MY_LIBRARY_SESSION_CODE_SAVE_EXPIRATION_DATE, trackLessonEdited),
	takeEvery([actionTypes.PRIVATE_LIBRARY_LEARN_MORE_PACKAGES, actionTypes.PRIVATE_LIBRARY_UNLOCK_SAMPLE_LESSONS, actionTypes.PRIVATE_LIBRARY_UNLOCK_OVERLAY_OPEN], trackContentPackageAccessEvent),
	takeEvery(actionTypes.LOGIN_NAVIGATE_TO_SIGNUP, trackLoginSignUpEvent),
	takeEvery(actionTypes.TOPBAR_SIGN_UP, trackTopbarSignUpEvent),
	takeEvery(actionTypes.TOPBAR_SIGN_IN, trackTopbarSignInEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_INITIALIZE_CONFIRM_SCHOOL_VIEW, trackAOXConfirmSchoolOpenEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRMED_SUCCESS, trackAOXConfirmSchoolYesEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_SCHOOL_CONFIRM_NO, trackAOXConfirmSchoolNoEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_INITIALIZE_CONFIRM_SCHOOL_NO, trackAOXConfirmSchoolNoOpenEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_INITIALIZE_INVITE_VIEW, trackAOXWelcomeInviteColleaguesEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_ACCEPT_WELCOME_INVITE, trackAOXSelectColleaguesEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_INVITE_COLLEAGUE, trackAOXInviteOpenEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_INVITE_USERS_SUCCESS, trackAOXInviteTeacherInvitesEvent),
	takeEvery([actionTypes.ADMIN_ONBOARDING_NAVIGATE_TO_STEP, actionTypes.ADMIN_ONBOARDING_SKIP_TO_LAST_STEP], trackSkipStepAOXEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_REFER_CONTACT_INITIALIZE, trackAdminOnboardingExpressReferContactOpenEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_REFER_A_CONTACT_SENT, trackReferAcontactSentEvent),
	takeEvery([actionTypes.ADMIN_ONBOARDING_SKIP_TO_LAST_STEP, actionTypes.ADMIN_ONBOARDING_REFER_A_CONTACT_SENT_SUCCESS], trackAdminOnboardingExpressYouDidItOpenEvent),
	takeEvery(actionTypes.ADMIN_ONBOARDING_CONGRATS_TO_NEARPOD_LIBRARY, trackAdminOnboardingExpressCongratsToNearpodLibraryEvent),
	takeEvery(actionTypes.SIGNUP_INITIALIZE, trackSignUpPageOpening),
	takeEvery(actionTypes.DM_STORAGE_EXPERIMENT_REFER_CONTACT_SEND, trackDMStorageExperimentReferContact),
	takeEvery(actionTypes.ADMIN_ONBOARDING_NO_STORE_DOWNLOADS_EXPLORE_LESSONS, trackNoStoreDownloadExploreLessons),
	takeEvery(actionTypes.ADMIN_ONBOARDING_NO_STORE_DOWNLOADS_GO_TO_MY_LIBRARY, trackNoStoreDownloadGoToMyLibrary),
	takeEvery([
		actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_COLLEAGUES_SUCCESS,
		actionTypes.ADMIN_ONBOARDING_ADD_TEACHERS_SHARE_CONFIRMED,
	], trackAddTeachersShareColleagues),
	takeEvery(actionTypes.ADMIN_ONBOARDING_NO_DM_INFORMATION_REFER_CONTACT_SEND, trackAdminOnboardingNoDMInformationReferContact),
	takeEvery(actionTypes.MY_LIBRARY_SUBS_OVERLAY_INITIALIZE, trackOverlaySubsInitialize),
	takeEvery(actionTypes.MY_LIBRARY_SUBS_OVERLAY_DISMISS, trackOverlaySubsDismiss),
	takeEvery(actionTypes.MY_LIBRARY_SUBS_OVERLAY_CLOSED, trackOverlaySubsClosed),
	takeEvery(actionTypes.MY_LIBRARY_SUBS_OVERLAY_LEARN_MORE, trackOverlaySubsLearnMore),
	takeEvery(actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_ADD_LESSON, trackAdminOnboardingPremiumActivitiesAddLesson),
	takeEvery([
		actionTypes.STUDENT_SUBMIT_OEQ_ANSWER,
		actionTypes.STUDENT_SUBMIT_OEQ_CONFIRMED,
	], trackStudentActivitySubmissionOEQ),
	takeEvery([actionTypes.STUDENT_QUIZ_SUBMIT_QUIZ, actionTypes.STUDENT_SUBMIT_QUIZ_CONFIRMED], trackStudentActivitySubmissionQuiz),
	takeEvery(actionTypes.SHARE_ENTITY_LINK, trackShareLinkByEntity),
	takeEvery(actionTypes.SHARE_ENTITY, trackShareByEntity),
	takeEvery(actionTypes.MY_LIBRARY_SESSION_SHARED, trackShareSession),
	takeEvery(actionTypes.MY_LIBRARY_GRADES_SUBJECTS_DISMISS, trackGradesSubjectsOverlayDismiss),
	takeEvery(actionTypes.MY_ACCOUNT_SAVE_TEACHER_INFO_SUCCESS, trackGradesSubjectsOverlaySubmitted),
	takeLatest(actionTypes.MY_LIBRARY_FIRST_LESSON_FILE_PICKED, trackFirstLessonOverlayFileImport),
	takeEvery(actionTypes.MY_LIBRARY_TIME_TO_CLIMB_EXPLORE, trackTimeToClimbOverlayExplore),
	takeEvery(actionTypes.MY_LIBRARY_TIME_TO_CLIMB_CANCELLED, trackTimeToClimbOverlayNoThanks),
	takeLatest(actionTypes.LOGIN_SEND_FORM, trackLoginEmail),
	takeLatest(actionTypes.LOGIN_SSO_REDIRECT, trackLoginSSO),
	takeLatest(actionTypes.MY_LIBRARY_INITIALIZE, trackCleverLogin),
	takeLatest(actionTypes.SIDEBAR_GO_TO_STORE_SELECTED, trackSidebarGoToBrowseLessons),
	takeLatest(actionTypes.SIDEBAR_GO_TO_PRIVATE_LIBRARY_SELECTED, trackSidebarGoToPrivateLibrary),
	takeLatest(actionTypes.SIDEBAR_GO_TO_PUBLIC_LIBRARY_ADMIN_SELECTED, trackSidebarGoToPublicLibrary),
	takeLatest(actionTypes.SIDEBAR_GO_TO_MY_ACTIVITIES_SELECTED, trackSidebarGoToMyActivities),
	takeLatest(actionTypes.SIDEBAR_GO_TO_SUB_PLANS_SELECTED, trackSidebarGoToSubPlansLibrary),
	takeLatest(actionTypes.SIDEBAR_GO_TO_REPORTS_SELECTED, trackSidebarGoToReports),
	takeLatest(actionTypes.SIDEBAR_GO_TO_TEACHER_SELECTED, trackSidebarGoToTeacherResources),
	takeLatest(actionTypes.MAIN_TOPBAR_HOME_SELECTED, trackGoToMyLibrary),
	takeLatest(actionTypes.MAIN_TOPBAR_LOGO_SELECTED, trackLogoSelected),
	takeEvery(actionTypes.BINDERS_ONBOARDING_GO_TO_STEP, trackBindersOnboarding),
	takeLatest([
		actionTypes.MAIN_TOPBAR_PLUS_ICON_TOGGLED,
		actionTypes.MY_LIBRARY_CREATION_MENU_CREATE_LESSON_SELECTED,
	], trackLessonCreationOpen),
	takeLatest(actionTypes.MAIN_TOPBAR_QUICK_LAUNCH_ICON_TOGGLED, trackToggleQuickLaunchIcon),
	takeEvery(actionTypes.FLOCABULARY_SAVE_SUCCESS, trackContentSaved),
	takeEvery(actionTypes.FLOCABULARY_LIMIT_REACHED_OVERLAY_NUDGE_SELECTED, trackLimitReachedOverlayNudgeMyAdminSelection),
	takeEvery(actionTypes.ONBOARDING_ACTIVITY_CREATE_LESSON_FAILED, trackOnboardingActivityCreateLessonFailed),
	takeEvery(actionTypes.ONBOARDING_ACTIVITY_ADD_ACTIVITY, trackOnboardingActivityAddActivity),
	takeEvery(actionTypes.ONBOARDING_ACTIVITY_ADD_ACTIVITY_CANCELLED, trackOnboardingActivitySkip),
	takeEvery(actionTypes.ONBOARDING_ACTIVITY_ADD_ACTIVITY_FAILED, trackOnboardingActivityAddActivityFailed),
	takeEvery(actionTypes.GOOGLE_SLIDES_SAVE_PRESENTATION_PROGRESSIVE_FAILED, trackGoogleSlidesSaveFailed),
	takeEvery(actionTypes.GOOGLE_SLIDES_INITIALIZE_UNKNOWN, trackGoogleSlidesInitializeSaveFailed),
	takeEvery(actionTypes.ADMIN_DASHBOARD_TOOLTIP_ACCEPTED, trackAdminDashboardTooltipClick),
	takeLatest(actionTypes.ONBOARDING_ACTIVITY_DEMO_CARD_POSTED, trackOnboardingActivityPostCard),
	takeLatest(actionTypes.MY_LIBRARY_LAUNCH_LIVE_LESSON, trackQuickTourStart),
	takeLatest(actionTypes.QUICK_LAUNCH_LAUNCH_LESSON_FAILED, trackQuickLaunchLaunchLessonFailed),
	takeLatest([
		actionTypes.GOALS_COLLABORATE_CARD_CREATE_LESSON,
		actionTypes.GOALS_TIME_TO_CLIMB_CARD_CREATE_LESSON,
		actionTypes.GOALS_OEQ_CARD_CREATE_LESSON_SUCCESS,
	], trackGoalsLessonCreated),
	takeLatest([
		actionTypes.ONBOARDING_ACTIVITY_ADD_ACTIVITY_SUCCESS,
		actionTypes.GOALS_TIME_TO_CLIMB_CARD_ENABLE_LESSON_SUCCESS,
		actionTypes.GOALS_OEQ_CARD_ENABLE_LESSON_SUCCESS,
	], trackGoalsLessonSaved),
	takeLatest([
		actionTypes.GOALS_COLLABORATE_CARD_LAUNCH_LIVE_LESSON,
		actionTypes.GOALS_TIME_TO_CLIMB_CARD_LAUNCH_LIVE_LESSON,
		actionTypes.GOALS_OEQ_CARD_LAUNCH_LIVE_LESSON,
	], trackGoalsLessonLaunched),
	takeLatest([
		actionTypes.IMMERSIVE_READER_DRAW_IT_TRANSFORM,
		actionTypes.IMMERSIVE_READER_OEQ_TRANSFORM,
		actionTypes.IMMERSIVE_READER_QUIZ_TRANSFORM,
		actionTypes.IMMERSIVE_READER_POLL_TRANSFORM,
		actionTypes.IMMERSIVE_READER_MATCHING_PAIRS_TRANSFORM,
		actionTypes.IMMERSIVE_READER_READ_ONLY_TRANSFORM,
	], trackImmersiveReaderOpened),
	takeLatest(
		actionTypes.QUICK_LAUNCH_TIMER_COUNTDOWN_START,
		trackQuickLaunchTimerStart
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_MAIN_ACCOUNT_SELECTED,
		trackNavigationMenuAccountSelection
	),
	takeLatest([
		actionTypes.NAVIGATION_MENU_NOTIFICATION_PREFERENCES_INITIALIZE,
		actionTypes.NAVIGATION_MENU_NOTIFICATION_PREFERENCES_SELECTED,
	], trackNotificationPreferencesSelected),
	takeLatest(
		actionTypes.NAVIGATION_MENU_NOTIFICATION_PREFERENCES_EMAIL_NOTIFICATION_ENABLED,
		trackNotificationPreferencesEmailSelected
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_MAIN_LOGOUT_SELECTED,
		trackNavigationMenuMainLogout
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_UPGRADE_SELECTED,
		trackNavigationMenuUpgrade
	),
	takeLatest(
		actionTypes.USER_SCHOOL_PROFILE_INITIALIZE,
		trackUserSchoolProfileOpen
	),
	takeLatest(
		actionTypes.USER_SCHOOL_PROFILE_SAVE,
		trackUserSchoolProfileSave
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_HELP_INITIALIZE,
		trackNavigationMenuHelpSelected
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_HELP_HELP_CENTER_SELECTED,
		trackNavigationMenuHelpCenterSelected
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_HELP_TEACHER_RESOURCES_SELECTED,
		trackNavigationMenuTeacherResourcesSelected
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_HELP_WHATS_NEW_SELECTED,
		trackNavigationMenuWhatsNewSelected
	),
	takeLatest(
		actionTypes.NAVIGATION_MENU_HELP_CONTACT_US_SELECTED,
		trackNavigationMenuContactUsSelected
	),
	takeLatest(
		actionTypes.STORE_INITIALIZE_VIDEO_PREVIEW,
		trackStorePreviewPageOpen
	),
	takeLatest(
		actionTypes.STORE_LAUNCH_VIDEO_PREVIEW,
		trackStoreLaunchFrontOfTheClassroom,
	),
	takeLatest([
		actionTypes.STUDENT_LEAD_SUBMIT_SUCCESS,
		actionTypes.STUDENT_LEAD_SUBMIT_FAILED,
	], trackSessionJoin),
	takeLatest(
		actionTypes.NAVIGATION_MENU_MAIN_SETTINGS_SELECTED,
		trackNavigationMenuSettingsSelection,
	),
	takeLatest([
		actionTypes.QUICK_LAUNCH_LAUNCH_LESSON_FAILED,
		actionTypes.MY_LIBRARY_LIVE_SESSION_FAILED,
		actionTypes.MY_LIBRARY_HOMEWORK_FAILED,
		actionTypes.MY_LIBRARY_LTI_SESSION_FAILED,
	], handleLessonLaunchFailed),
	takeLatest(actionTypes.USER_COOKIE_BLOCKED_FAILED, trackCookieFailed),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_SESSION_USER_LEFT, trackInteractiveVideoPlayEvent),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE,
		actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE_CONFIRM_ACCEPTED,
	], trackInteractiveVideoInteractiveModeToggle),
	takeEvery(
		actionTypes.GOOGLE_SLIDES_SAVE_PRESENTATION_PROGRESSIVE_START_FAILED,
		handleGoogleSlidesSaveFailed,
	),
	takeLatest(actionTypes.NICKEL_TOUR_CLOSE, trackNickelTourClose),
	takeLatest(actionTypes.NICKEL_TOUR_COMPLETED, trackNickelTourComplete),
	takeLatest(actionTypes.GOALS_COLLABORATE_CARD_TOOLTIP_SELECTED, trackNickelTourTooltipDismiss),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_SAVE,
		actionTypes.VIDEO_SAVE,
	], trackVideoSavedEvent),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_CANCEL,
	], trackInteractiveVideoUploadCancelEvent),
	takeEvery(actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_PICKED, trackSourceVideoPickedEvent),
	takeEvery(actionTypes.VIDEO_STORE_LESSON_SELECTED, trackStoreVideoPickedEvent),
	takeEvery(actionTypes.VIDEO_MY_VIDEOS_LESSON_SELECTED, trackMyVideosPickedEvent),
	fork(trackUserFirstLogin),
	takeEvery(actionTypes.VIDEO_HANDOUT_SELECTED, trackHandoutSelected),
	takeLatest([
		actionTypes.MAIN_TOPBAR_CREATE_LESSON,
		actionTypes.MAIN_TOPBAR_CREATE_GOOGLE_DRIVE_LESSON,
		actionTypes.MAIN_TOPBAR_CREATE_INTERACTIVE_VIDEO,
		actionTypes.MY_LIBRARY_CREATE_LESSON,
		actionTypes.MY_LIBRARY_CREATE_GOOGLE_DRIVE_LESSON,
		actionTypes.MY_LIBRARY_CREATE_INTERACTIVE_VIDEO,
		actionTypes.ONBOARDING_GUIDE_LESSON_BUILDER_CARD_SELECTED,
		actionTypes.MY_LIBRARY_CREATE_LESSON_THUMBNAIL,
	], trackLessonCreation),
	takeLatest([
		actionTypes.MY_LIBRARY_EXPLORE_STORE,
	], trackExploreLibraryThumbnail),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CREATION_EMBED_IN_SLIDE, trackEmbedInSlidesSelected),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CAPTIONS_LANGUAGE_SELECTED, trackCaptionsLenguageSelected),
	takeEvery([
		actionTypes.INTERACTIVE_VIDEO_SUBMIT_MCQ_ANSWER_SUCCESS,
		actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_SUCCESS,
	], trackInteractiveVideoSubmissionSucceeded),
	takeLatest(actionTypes.OEQ_QUESTION_UPDATE_ERROR, trackSaveQuestionFailed),
	takeLatest(actionTypes.ONBOARDING_GUIDE_EXPLORE_LESSONS_CARD_SELECTED, trackOnboardingGuideExploreLessons),
	takeLatest(actionTypes.MY_LIBRARY_DELETE_FOLDER_RESPONSE_SUCCESS, trackMylibraryFolderDeleted),
	takeLatest(actionTypes.MY_LIBRARY_DELETE_LESSON_SUCCESS, trackMyLibraryLessonDeleted),
	takeEvery([
		actionTypes.INTERACTIVE_VIDEO_SUBMIT_MCQ_ANSWER_FAILED,
		actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_FAILED,
	], trackInteractiveVideoSubmissionFailed),
	takeLatest([
		actionTypes.PAYWALLS_CREATE_GOOGLE_SLIDES_UNAUTHORIZED,
	], trackMyLibraryGoogleSlidesPaywallClick),
	takeLatest([
		actionTypes.PAYWALLS_ADDON_GOOGLE_SLIDES_UPGRADE,
	], trackAddonGoogleSlidesPaywallClick),
	takeLatest(actionTypes.SESSION_PREVIEW_EXITED, trackExitSessionPreview),
	takeEvery(
		actionTypes.SESSION_SETTINGS_STREAM_MODE_UPDATE,
		trackStreamModeUpdated,
	),
	takeLatest(actionTypes.ACTIVITY_BANKS_ACTIVITY_SELECTED, trackActivityBanksActivitySelected),
]

export {
	trackVideoSavedEvent,
	trackActivitySavedEvent,
	trackReferenceMediaAddedEvent,
	trackActivitySubmission,
	trackLessonAddedFromLibrary,
	trackLessonAddedToFolder,
	getMetrics,
	trackLessonEdited,
	trackOverlayCreateLessonOnboardingConfirm,
	trackOverlayCreateLessonOnboardingDismiss,
	trackOverlayCreateLessonOnboardingClose,
	trackOverlayCreateLessonOnboardingInitialize,
	trackSchoolUserOnboardingOverlayClose,
	trackSchoolUserOnboardingOverlayInitialize,
	trackSchoolUserOnboardingOverlayConfirm,
	trackSchoolUserOnboardingOverlayDismiss,
	trackOverlaySchoolTrialOnboardingConfirm,
	trackOverlaySchoolTrialOnboardingDismiss,
	trackOverlaySchoolTrialOnboardingInitialize,
	trackOverlaySchoolTrialOnboardingClose,
	trackLessonSaved,
	trackLessonLaunchedHomework,
	trackExtensionLessonLaunched,
	trackFolderCreated,
	trackOverlaySubsInitialize,
	trackOverlaySubsDismiss,
	trackOverlaySubsClosed,
	trackOverlaySubsLearnMore,
	trackShareLessonLinkFromLibrary,
	trackShareLessonLink,
	trackLoginSignUpEvent,
	trackTopbarSignUpEvent,
	trackSignUpPageOpening,
	trackTopbarSignInEvent,
	trackDMStorageExperimentReferContact,
	identifyUser,
	trackUpgradeAction,
	trackSkipStepAOXEvent,
	trackContentPackageAccessEvent,
	trackGradesSubjectsOverlayDismiss,
	trackGradesSubjectsOverlaySubmitted,
	trackShareStoreFront,
	trackFirstLessonOverlayFileImport,
	trackTimeToClimbOverlayExplore,
	trackTimeToClimbOverlayNoThanks,
	trackSchoolInfoUpdated,
	trackLoginEmail,
	trackLoginSSO,
	trackCleverLogin,
	trackSidebarGoToBrowseLessons,
	trackSidebarGoToPrivateLibrary,
	trackSidebarGoToPublicLibrary,
	trackSidebarGoToMyActivities,
	trackSidebarGoToSubPlansLibrary,
	trackSidebarGoToReports,
	trackSidebarGoToTeacherResources,
	trackGoToMyLibrary,
	trackBindersOnboarding,
	trackLessonCreationOpen,
	trackToggleQuickLaunchIcon,
	trackShareSession,
	trackContentSaved,
	trackOnboardingActivityCreateLessonFailed,
	trackOnboardingActivityAddActivity,
	trackOnboardingActivitySkip,
	trackOnboardingActivityAddActivityFailed,
	trackOnboardingActivityPostCard,
	trackQuickTourStart,
	trackLimitReachedOverlayNudgeMyAdminSelection,
	trackQuickLaunchLaunchLessonFailed,
	trackGoalsLessonCreated,
	trackGoalsLessonSaved,
	trackGoalsLessonLaunched,
	trackImmersiveReaderOpened,
	trackAdminDashboardTooltipClick,
	trackQuickLaunchTimerStart,
	trackNavigationMenuAccountSelection,
	trackNotificationPreferencesSelected,
	trackNotificationPreferencesEmailSelected,
	trackNavigationMenuMainLogout,
	trackNavigationMenuUpgrade,
	trackUserSchoolProfileOpen,
	trackUserSchoolProfileSave,
	trackNavigationMenuHelpSelected,
	trackNavigationMenuHelpCenterSelected,
	trackNavigationMenuTeacherResourcesSelected,
	trackNavigationMenuWhatsNewSelected,
	trackNavigationMenuContactUsSelected,
	trackStorePreviewPageOpen,
	trackStoreLaunchFrontOfTheClassroom,
	trackInteractiveVideoInteractiveModeToggle,
	trackCookieFailed,
	trackSessionJoin,
	trackInteractiveVideoPlayEvent,
	handleGoogleSlidesSaveFailed,
	trackNickelTourClose,
	trackNickelTourComplete,
	trackNickelTourTooltipDismiss,
	trackStoreVideoPickedEvent,
	trackMyVideosPickedEvent,
	trackHandoutSelected,
	trackSourceVideoPickedEvent,
	trackVideoPickedEvent,
	getChameleonUserProperties,
	trackGoogleSlidesAuthorizeError,
	trackExploreLibraryThumbnail,
	trackLessonCreation,
	trackEmbedInSlidesSelected,
	trackCaptionsLenguageSelected,
	trackInteractiveVideoUploadCancelEvent,
	trackVideoUploadEvent,
	trackInteractiveVideoSubmissionSucceeded,
	trackSaveQuestionFailed,
	trackOnboardingGuideExploreLessons,
	trackMylibraryFolderDeleted,
	trackMyLibraryLessonDeleted,
	trackInteractiveVideoSubmissionFailed,
	trackMyLibraryGoogleSlidesPaywallClick,
	trackAddonGoogleSlidesPaywallClick,
	trackExitSessionPreview,
	trackStreamModeUpdated,
	trackActivityBanksActivitySelected,
}
